import React, { useEffect, useState, useRef } from 'react';
import caseService from '../../services/caseService';
import CreateCaseModal from './CreateCaseModal';
import { formatDate } from '../../utils/dateUtils';

const CaseList = ({ onCaseSelect }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // Local state for input
  const [filters, setFilters] = useState({
    status: 'active',
    practice_area: '',
    search: '',
  });
  const debounceRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput }));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    loadCases();
  }, [filters]);

  const loadCases = async () => {
    try {
      // Only show loading spinner on initial load, not during search
      if (cases.length === 0) setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.practice_area) params.practice_area = filters.practice_area;
      if (filters.search) params.search = filters.search;
      
      const data = await caseService.getCases(params);
      // API returns array directly
      setCases(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    setShowCreateModal(false);
    await loadCases();
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-300 border border-green-500/30',
      pending: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      on_hold: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
      closed: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
      archived: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30',
    };
    return colors[status] || 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
  };

  if (loading && cases.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Filters and Actions */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4 mb-6 backdrop-blur-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <input
              type="text"
              placeholder="Search cases..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
            </select>

            {/* Practice Area Filter */}
            <input
              type="text"
              placeholder="Practice area..."
              value={filters.practice_area}
              onChange={(e) => setFilters({ ...filters, practice_area: e.target.value })}
              className="px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium whitespace-nowrap shadow-lg shadow-cyan-500/30 transition-all"
          >
            + New Case
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-6 backdrop-blur-sm">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Cases Table */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 overflow-hidden backdrop-blur-sm">
        <table className="min-w-full divide-y divide-virgo-navy-600">
          <thead className="bg-virgo-navy-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Case Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Case Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Practice Area
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-virgo-navy-600">
            {cases.map((caseItem) => (
              <tr
                key={caseItem.id}
                className="hover:bg-virgo-navy-600/50 cursor-pointer transition-colors"
                onClick={() => onCaseSelect(caseItem.id)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-cyan-400">
                  {caseItem.display_number}
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="font-medium text-white">{caseItem.case_name}</div>
                  {caseItem.client_name && (
                    <div className="text-virgo-teal-400 text-xs">{caseItem.client_name}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-300">
                  {caseItem.practice_area || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(caseItem.status)}`}>
                    {String(caseItem.status || '').replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                  {formatDate(caseItem.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCaseSelect(caseItem.id);
                    }}
                    className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    View →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {cases.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-virgo-teal-400">No cases found</p>
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (
        <CreateCaseModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateCase}
        />
      )}
    </div>
  );
};

export default CaseList;
